import { Answer, Section } from "@appally/collections/Assessment";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type TemporaryAnswerPagePayload = {
  assessmentId: string;
  section: Section;
};

type SetTemporaryAnswerPagePayload = TemporaryAnswerPagePayload & {
  page: number;
};

export type TemporaryAnswersState = {
  answers: Answer[];
  colorMode: "light" | "dark";
  lastPages?: Record<string, number>;
};

const initialState: TemporaryAnswersState = {
  answers: [],
  colorMode: "light",
  lastPages: {},
};

export const getTemporaryAnswerPageKey = (
  assessmentId: string,
  section: Section,
) => `${assessmentId}:${section}`;

const assessmentSlice = createSlice({
  name: "temporaryAnswers",
  initialState,
  reducers: {
    setTemporaryAnswers: (state, action: PayloadAction<Answer[]>) => {
      state.answers = action.payload;
    },
    updateTemporaryAnswer: (state, action: PayloadAction<Answer>) => {
      const answer = action.payload;
      const filteredAnswers = state.answers.filter(
        (a) => a.question_id !== answer.question_id
      );
      state.answers = [...filteredAnswers, answer];
    },
    clearTemporaryAnswers: (state) => {
      state.answers = [];
    },
    clearTemporaryAnswersForSection: (
      state,
      action: PayloadAction<Section>,
    ) => {
      state.answers = state.answers.filter(
        (answer) => answer.section !== action.payload,
      );
    },
    setColorMode: (state, action: PayloadAction<"light" | "dark">) => {
      state.colorMode = action.payload;
    },
    setTemporaryAnswerPage: (
      state,
      action: PayloadAction<SetTemporaryAnswerPagePayload>,
    ) => {
      state.lastPages = state.lastPages ?? {};
      state.lastPages[
        getTemporaryAnswerPageKey(
          action.payload.assessmentId,
          action.payload.section,
        )
      ] = action.payload.page;
    },
    clearTemporaryAnswerPage: (
      state,
      action: PayloadAction<TemporaryAnswerPagePayload>,
    ) => {
      state.lastPages = state.lastPages ?? {};
      delete state.lastPages[
        getTemporaryAnswerPageKey(
          action.payload.assessmentId,
          action.payload.section,
        )
      ];
    },
  },
});

export const {
  setTemporaryAnswers,
  updateTemporaryAnswer,
  clearTemporaryAnswers,
  clearTemporaryAnswersForSection,
  setColorMode,
  setTemporaryAnswerPage,
  clearTemporaryAnswerPage,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
